/* eslint-disable @typescript-eslint/no-explicit-any */

import { generateSignatureFieldMetadata } from '@/lib/pdf';
import { updateBlockText } from '@/lib/pdf/text-block-mapper';
import { applyVirtualBlockEdit, isVirtualBlockId } from '@/lib/pdf/virtual-blocks';

import { ConfirmationModal } from '@/components/core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { LetterheadUploadDialog } from './LetterheadUploadDialog';
import PreparingContract from './PreparingContract';
import { extractSignatories } from './utils';

import {
  generatePdfForDownload,
  generateProtectedPdfForDownload,
} from '@/modules/templates/services/pdf.service';
import { signatureRollout } from '@/modules/templates/services/signature.service';
import { LegalDocument, LetterheadConfig } from '@/schemas/schema';

import {
  trackDocumentDownloaded,
  trackDocumentEdited,
  trackDocumentSentSelise,
  trackSignatureFieldAdded,
} from '@/lib/analytics';

import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DownloadPDFDialog from './DownloadPDFDialog';
import { type SignatureField } from './SignatureFieldOverlay';
import TemplatePDFReviewHeader, { type SaveDraftStatus } from './TemplatePDFReviewHeader';
import { saveTemplateReview, loadTemplateReview } from './TemplateReviewStorage';
import { updateContractDocument } from '@/modules/org/contracts/services/contract-document.service';
import type { OrgContractDocumentUpdateInput } from '@/modules/org/contracts/types/contract-document.types';
import {
  orgContractDocumentByIdQueryKey,
  useGetOrgContractDocumentById,
} from '@/graphql';
import { useGetContractDocumentEvents } from '@/modules/org/contracts/hooks/use-contract-document-events';
import type { DocumentStatus } from '@/modules/org/contracts/constants/mock-contract-documents';
import { useResolvedDefaultLetterhead } from '@/modules/org/org-letterheads/hooks/use-resolved-default-letterhead';
import PaymentDialog from '@/modules/payment/PaymentDialog';

// New modular pieces
import { useDocumentHistory } from './hooks/useDocumentHistory';
import { usePdfPreview } from './hooks/usePdfPreview';
import { useBlockHover } from './hooks/useBlockHover';
import { useReviewPremium } from './hooks/useReviewPremium';
import { type ReviewBlock, type ReviewHighlight } from './adapter/types';
import {
  buildLegalDocumentOutline,
  fromReviewBlock,
  toReviewBlock,
} from '@/lib/pdf/review-outline';
import { PdfReviewViewer } from './PdfReviewViewer';
import { ReviewShell } from './ReviewShell';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { ReviewSidebar } from './ReviewSidebar';
import { SidebarFooter } from './SidebarFooter';
import { GuestPaywallBanner } from './GuestPaywallBanner';
import { PdfPreviewToolbar } from './PdfPreviewToolbar';
import { MobileActionBar } from './MobileActionBar';

interface TemplatePDFReviewProps {
  document: LegalDocument;
  formData: Record<string, any>;
  templateSlug: string;
  templateTitle: string;
  onBack?: () => void;
  hideDocumentActions?: boolean;
}

const TemplatePDFReview = ({
  document,
  formData,
  templateSlug,
  templateTitle,
  onBack,
  hideDocumentActions = false,
}: TemplatePDFReviewProps) => {
  const router = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { resolvedLetterhead, isLoading: isLoadingOrgLetterheads } = useResolvedDefaultLetterhead();
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);
  const [isContractApproved, setIsContractApproved] = useState(false);
  const [isApprovingContract, setIsApprovingContract] = useState(false);

  // Guest paywall — documentId is the OrgContractDocument ItemId saved by
  // useAutoSaveDraft into the URL `draft` param.
  const draftId = searchParams.get('draft') ?? searchParams.get('document');

  // Premium gating adapter — maps per-feature gating onto the existing
  // Blocks paywall (authenticated/paid → unlocked; unpaid guest → gated).
  const {
    isAuthenticated,
    isGuestUnpaid,
    isPremiumUnlocked,
    showPaymentDialog,
    setShowPaymentDialog,
    triggeredFeature,
    requestPremiumAccess,
    dismissPaymentDialog,
    onPaid: onPremiumPaid,
  } = useReviewPremium(draftId);

  // Fetch the OrgContractDocument row so we can read its current Status —
  // editing / send / letterhead are gated on Status being draft or
  // generation_completed.
  const { data: contractDoc } = useGetOrgContractDocumentById(draftId ?? '', !!draftId);
  const documentStatus = (contractDoc?.Status as DocumentStatus | undefined) ?? undefined;
  const isEditable =
    !documentStatus || documentStatus === 'draft' || documentStatus === 'generation_completed';
  const isPendingApproval =
    contractDoc?.HasSentForApproval === true && contractDoc?.IsApproved !== true;
  const isEditDisabled =
    (hideDocumentActions && isContractApproved) ||
    (!hideDocumentActions && contractDoc?.HasSentForApproval === true);
  const canEditContract =
    isEditable && !isEditDisabled;

  useEffect(() => {
    setIsContractApproved(contractDoc?.IsApproved === true);
  }, [contractDoc?.IsApproved]);

  const handleApproveContract = async () => {
    if (isApprovingContract) return;

    if (!draftId) {
      toast({
        variant: 'destructive',
        title: 'Unable to approve contract',
        description: 'The contract document could not be identified.',
      });
      return;
    }

    try {
      setIsApprovingContract(true);
      await updateContractDocument(draftId, { IsApproved: true });
      void queryClient.invalidateQueries({
        queryKey: orgContractDocumentByIdQueryKey(draftId),
      });
      setIsContractApproved(true);
      setShowApprovalConfirmation(false);
      toast({
        variant: 'success',
        title: t('GENERATEREVIEW_TOAST_CONTRACTAPPROVED'),
        description: t('GENERATEREVIEW_TOAST_CONTRACTAPPROVED_SUCCESSFULLY'),
      });
    } catch (error) {
      console.error('Failed to approve contract', error);
      toast({
        variant: 'destructive',
        title: 'Unable to approve contract',
        description: 'Please try again.',
      });
    } finally {
      setIsApprovingContract(false);
    }
  };

  // Once `document_completed`, the SELISE webhook backfills SigDocumentUrl with
  // the finalized signed PDF. We swap the locally-generated preview for it and
  // route Download to it as well.
  const sigDocumentUrl = contractDoc?.SigDocumentUrl ?? null;
  const useFinalizedPdf =
    ['signatory_signed_success', 'document_completed'].includes(documentStatus ?? '') &&
    !!sigDocumentUrl;

  // Signature events → annotate signatories in the sidebar.
  const { data: eventsResult } = useGetContractDocumentEvents(draftId ?? '', !!draftId);
  const signatoryEventByEmail = useMemo(() => {
    const map = new Map<string, { eventType: string; occurredAt: string; signedTime?: string }>();
    (eventsResult?.items ?? []).forEach((e) => {
      if (
        (e.EventType !== 'signatory_signed_success' && e.EventType !== 'signatory_signed_failed') ||
        !e.SignatoryEmail
      ) {
        return;
      }
      const existing = map.get(e.SignatoryEmail);
      const occurredAt = e.OccurredAt ?? e.CreatedDate ?? '';
      if (!existing || occurredAt > existing.occurredAt) {
        map.set(e.SignatoryEmail, {
          eventType: e.EventType,
          occurredAt,
          signedTime: e.SignedTime,
        });
      }
    });
    return map;
  }, [eventsResult?.items]);

  // Manual "Save as Draft" state.
  const [saveDraftStatus, setSaveDraftStatus] = useState<SaveDraftStatus>('idle');
  const saveDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<string>('details');
  const [isPreparingContract, setIsPreparingContract] = useState(false);
  const [showDownloadPrompt, setShowDownloadPrompt] = useState(false);
  // Separate from `isPreparingContract`, which blocks the whole page for the
  // send-for-signature flow. A download only needs the button to say it's busy.
  const [isDownloading, setIsDownloading] = useState(false);
  // Tracked separately from `isDownloading` so the two download buttons show
  // their own spinners; rasterising every page is the slower of the two.
  const [isDownloadingProtected, setIsDownloadingProtected] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  // Per-slot page-element maps for the double-buffered viewer (active + buffer).
  const pageRefsA = useRef<Map<number, HTMLDivElement>>(new Map());
  const pageRefsB = useRef<Map<number, HTMLDivElement>>(new Map());
  // Finalized (already-signed) PDF page count — that branch isn't buffered.
  const [finalizedNumPages, setFinalizedNumPages] = useState<number | null>(null);

  // Signature field overlay state
  const [signatureFields, setSignatureFields] = useState<SignatureField[]>([]);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // Text editing history — undo/redo of the editable document.
  const {
    current: editableDocument,
    canUndo,
    canRedo,
    push: pushDocument,
    undo: undoDocument,
    redo: redoDocument,
    reset: resetDocument,
  } = useDocumentHistory(document);

  const [isEditing, setIsEditing] = useState(false);
  const [editingBlock, setEditingBlock] = useState<ReviewBlock | null>(null);
  const [isEditingEffectiveDate, setIsEditingEffectiveDate] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editedBlockIds, setEditedBlockIds] = useState<Set<string>>(new Set());
  const [activeEditBlock, setActiveEditBlock] = useState<ReviewHighlight | null>(null);
  // Transient "navigated-to" block + replay nonce for the outline-click pulse.
  const [flashBlock, setFlashBlock] = useState<ReviewHighlight | null>(null);
  const [flashNonce, setFlashNonce] = useState(0);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // In-flight custom smooth-scroll animation frame (so a new nav cancels it).
  const scrollAnimRef = useRef<number | null>(null);
  // Edit-tab scroller + remembered outline scroll position, so returning from
  // the block editor lands back where the user was in the outline.
  const editTabScrollRef = useRef<HTMLDivElement>(null);
  const outlineScrollTopRef = useRef(0);
  // Outline section collapse state, kept here so it survives the panel
  // unmounting while the block editor is open.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const handleToggleSection = useCallback((id: string, open: boolean) => {
    setOpenSections((prev) => (prev[id] === open ? prev : { ...prev, [id]: open }));
  }, []);

  // Sidebar editor dirty/text tracking (so we can guard block switches).
  const editorDirtyRef = useRef(false);
  const editorTextRef = useRef<string>('');

  useEffect(() => {
    if (!isEditDisabled) return;

    setSidebarTab('details');
    setIsEditing(false);
    setIsEditingEffectiveDate(false);
    setEditingBlock(null);
    setActiveEditBlock(null);
    editorDirtyRef.current = false;
  }, [isEditDisabled]);
  const [pendingBlock, setPendingBlock] = useState<{
    block: ReviewBlock;
    at: ReviewHighlight | null;
  } | null>(null);

  // PDF preview lifecycle — double-buffered so edits swap in with no loader
  // and no scroll jump.
  const {
    slotUrls,
    activeSlot,
    numPages: activeNumPages,
    isLoadingPdf,
    isPdfStale,
    generatePreview,
    regenerateNow,
    handleSlotLoaded,
    handleSlotReady,
    hasPdf,
  } = usePdfPreview({ formData, templateSlug, scrollContainerRef: containerRef });

  // Active slot's page-element map drives hover + signature overlays.
  const activePageRefs = activeSlot === 'a' ? pageRefsA : pageRefsB;
  // The currently-displayed PDF blob URL (used for send-for-signature).
  const activePdfUrl = slotUrls[activeSlot];
  // UI page count: finalized branch uses its own; otherwise the active slot's.
  const numPages = useFinalizedPdf ? finalizedNumPages : activeNumPages;

  // A stable-ish signature for detecting meaningful document changes even if
  // the parent recreates the document object every render.
  const documentSignature = useMemo(() => {
    const md = document?.metadata;
    return [
      md?.documentType ?? '',
      md?.title ?? '',
      md?.effectiveDate ?? '',
      md?.jurisdiction ?? '',
      md?.generatedAt ?? '',
      String(document?.content?.length ?? 0),
      String(document?.signatories?.length ?? 0),
      document?.letterhead?.imageDataUrl ? 'letterhead:1' : 'letterhead:0',
    ].join('|');
  }, [document]);

  const lastDocumentSignatureRef = useRef<string | null>(null);

  // Letterhead state
  const [showLetterheadDialog, setShowLetterheadDialog] = useState(false);

  // Normalized views of the document for the model-agnostic sidebar.
  const outline = useMemo(
    () => buildLegalDocumentOutline(editableDocument, t),
    [editableDocument, t]
  );
  const reviewDetails = useMemo(
    () => ({
      title: editableDocument.metadata?.title ?? '',
      effectiveDate: editableDocument.metadata?.effectiveDate,
      jurisdiction: editableDocument.metadata?.jurisdiction,
    }),
    [editableDocument]
  );

  const signatories = extractSignatories(editableDocument, formData, {
    defaultRole: t('GENERATEREVIEW_DEFAULTS_SIGNATORYROLE'),
    companyName: t('GENERATEREVIEW_DEFAULTS_COMPANYNAME'),
    authorizedRepresentativeRole: t('GENERATEREVIEW_DEFAULTS_AUTHORIZEDREPRESENTATIVE'),
    employeeRole: t('GENERATEREVIEW_DEFAULTS_EMPLOYEEROLE'),
  });

  // Block hover — extracted hook (throttle, dedup, paragraph-boundary detection).
  // Disabled once the document is locked or showing the finalized signed PDF.
  const {
    hoveredBlock,
    handleMouseMove: handleTextHover,
    handleMouseLeave: handleTextLeave,
    clearHover,
    resolveBlockFromTouch,
    locateBlock,
  } = useBlockHover({
    document: editableDocument,
    pageRefs: activePageRefs,
    effectiveDateLabel: t('GENERATEREVIEW_DEFAULTS_EFFECTIVEDATELABEL'),
    enabled: isEditable && !useFinalizedPdf,
    totalPages: numPages,
  });

  // Persist a freshly-edited document back into sessionStorage so a refresh
  // restores the edited version. Reads any existing entry first to preserve
  // fields we didn't touch.
  const persistReviewSnapshot = useCallback(
    (doc: LegalDocument) => {
      const existing = loadTemplateReview(templateSlug);
      saveTemplateReview(templateSlug, {
        document: doc,
        formData,
        templateId: existing?.templateId ?? doc.metadata?.documentType ?? templateSlug,
        templateSlug,
        templateTitle,
      });
    },
    [formData, templateSlug, templateTitle]
  );

  // (The pdf.js worker is configured by review/react-pdf-lazy.ts, which every
  // react-pdf consumer lazy-imports — no per-page setup needed.)

  // Track container width for responsive PDF rendering
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      const padding = width < 640 ? 32 : 48;
      setContainerWidth(width - padding);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const convertMetadataToFields = (
    metadataFields: any[],
    actualPageCount: number
  ): SignatureField[] => {
    return metadataFields.map((field) => {
      const signatoryIndex = field.signatoryIndex ?? (field.party === 'employer' ? 0 : 1);
      const pageNumber = Math.min(field.pageNumber, actualPageCount);
      return {
        id: field.id,
        type: field.type === 'text' ? 'signature' : field.type,
        signatoryIndex,
        pageNumber,
        x: field.x,
        y: field.y,
        width: field.width,
        height: field.height,
        label: field.label,
      };
    });
  };

  // Finalized (already-signed) PDF branch — single Document, not buffered.
  const onFinalizedLoadSuccess = ({ numPages }: { numPages: number }) => {
    setFinalizedNumPages(numPages);
  };

  const handleDownloadPdf = async () => {
    setShowDownloadPrompt(false);
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      if (useFinalizedPdf && sigDocumentUrl) {
        const response = await fetch(sigDocumentUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch signed PDF: ${response.status}`);
        }
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = `${templateSlug}-signed.pdf`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        await generatePdfForDownload(editableDocument, formData, templateSlug);
      }
      trackDocumentDownloaded(templateSlug);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('GENERATEREVIEW_ALERTS_DOWNLOADFAILED'),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Image-only export with no extractable text. Deliberately skips the
   * DownloadPDFDialog "send for signature instead?" prompt — that nudge is
   * about signing, which doesn't apply to a protected reference copy.
   */
  const handleProtectedDownload = async () => {
    if (isDownloadingProtected) return;
    setIsDownloadingProtected(true);
    try {
      if (useFinalizedPdf && sigDocumentUrl) {
        // Already-signed documents are stored server-side; protect those bytes
        // rather than re-rendering from the editable draft.
        const response = await fetch(sigDocumentUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch signed PDF: ${response.status}`);
        }
        const { buildProtectedPdf } = await import('@/lib/pdf/protected-pdf');
        const blob = await buildProtectedPdf(await response.blob());
        const objectUrl = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = `${templateSlug}-signed-protected.pdf`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        await generateProtectedPdfForDownload(editableDocument, formData, templateSlug);
      }
      trackDocumentDownloaded(templateSlug);
    } catch (error) {
      console.error('Error downloading protected PDF:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('GENERATEREVIEW_ALERTS_DOWNLOADFAILED'),
      });
    } finally {
      setIsDownloadingProtected(false);
    }
  };

  const requestProtectedDownload = useCallback(() => {
    if (!requestPremiumAccess('download')) return;
    void handleProtectedDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPremiumAccess]);

  // Download trigger, gated for unpaid guests.
  const requestDownload = useCallback(() => {
    if (!requestPremiumAccess('download')) return;
    if (!isEditable) {
      void handleDownloadPdf();
    } else {
      setShowDownloadPrompt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPremiumAccess, isEditable]);

  const handleSendToSignature = async () => {
    if (!activePdfUrl || !formData || signatureFields.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('GENERATEREVIEW_ALERTS_SIGNATURENOTREADY'),
      });
      return;
    }

    // Gate when an unpaid guest has applied edits/letterhead — signing the
    // *plain* document stays free; signing *with their changes* is premium.
    const hasEdits = editedBlockIds.size > 0;
    const hasLetterhead = !!editableDocument.letterhead;
    if ((hasEdits || hasLetterhead) && !isPremiumUnlocked) {
      requestPremiumAccess('signature-with-edits');
      return;
    }

    setIsPreparingContract(true);

    try {
      const DPI_SCALE = 96 / 72;

      const signatureFieldsForAPI = signatureFields.map((field) => {
        const pageNumber = field.pageNumber || 1;
        const x = field.x * DPI_SCALE;
        const y = field.y * DPI_SCALE;
        const width = field.width * DPI_SCALE;
        const height = field.height * DPI_SCALE;
        return {
          id: field.id,
          type: field.type,
          signatoryIndex: field.signatoryIndex,
          pageNumber,
          x: Math.round(x),
          y: Math.round(y),
          width: Math.round(width),
          height: Math.round(height),
          label: field.label || `${field.type === 'signature' ? 'Signature' : 'Date'}`,
        };
      });

      const signatoriesForAPI = signatories.map((sig, idx) => {
        const docSignatory = document.signatories?.[idx];
        return {
          party: docSignatory?.party || sig.role || `signatory_${idx}`,
          name: sig.name,
          email: sig.email,
          role: sig.role,
        };
      });

      const pdfBlob = await fetch(activePdfUrl).then((res) => res.blob());
      const pdfBuffer = await pdfBlob.arrayBuffer();
      const bytes = new Uint8Array(pdfBuffer);
      const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
      const pdfBase64 = btoa(binary);

      const rolloutResult = await signatureRollout({
        pdfBase64,
        document,
        formData,
        signatories: signatoriesForAPI,
        signatureFields: signatureFieldsForAPI,
        templateSlug,
        templateTitle,
        numPages: numPages || 1,
        draftId: draftId || '',
      });

      trackDocumentSentSelise(templateSlug, signatoriesForAPI.length);

      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('signature-last-result', JSON.stringify(rolloutResult));
        window.sessionStorage.setItem(
          'signature-last-payload',
          JSON.stringify({
            document,
            formData,
            signatories: signatoriesForAPI,
            templateSlug,
            templateTitle,
          })
        );
      }

      setIsPreparingContract(false);
      const successUrl = draftId
        ? `/templates/${templateSlug}/review/success?document=${encodeURIComponent(draftId)}`
        : `/templates/${templateSlug}/review/success`;
      router(successUrl);
    } catch (error) {
      console.error('Error preparing for signature:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : t('GENERATEREVIEW_ALERTS_SENDFAILED'),
      });
      setIsPreparingContract(false);
    }
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));
  const handleFitWidth = () => {
    if (containerRef.current && containerWidth) {
      const pageWidthInPixels = 612;
      setScale(containerWidth / pageWidthInPixels);
    }
  };
  const handleFitPage = () => setScale(1.0);

  // PDF page sizing tracks the letterhead page size (fallback LETTER 612×792).
  const docPageWidthPt = editableDocument?.letterhead?.pageWidth ?? 612;
  const docPageHeightPt = editableDocument?.letterhead?.pageHeight ?? 792;
  const pageWidth =
    containerWidth && containerWidth < docPageWidthPt ? containerWidth : docPageWidthPt;
  const pageHeight = (pageWidth / docPageWidthPt) * docPageHeightPt;

  const scrollToPage = (page: number) => {
    setPageNumber(page);
    // Target the active slot's page element (both buffer layers carry
    // data-page-number, so a document-wide query could hit the hidden one).
    const pageElement = activePageRefs.current.get(page);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSignatureFieldsChange = (fields: SignatureField[]) => {
    const previousLength = signatureFields.length;
    setSignatureFields(fields);
    if (fields.length > previousLength) {
      const newField = fields[fields.length - 1];
      trackSignatureFieldAdded(templateSlug, newField.type || 'signature');
    }
  };

  // ---- Editing flow -------------------------------------------------------

  // Opens a block for editing in the sidebar. Gated for unpaid guests.
  const openBlockForEditing = useCallback(
    (block: ReviewBlock, at: ReviewHighlight | null) => {
      if (!canEditContract) return;
      if (!requestPremiumAccess('edit')) return;
      // Remember the outline's scroll position so closing the editor returns
      // the user to where they were instead of jumping back to the top.
      if (!isEditing) {
        outlineScrollTopRef.current = editTabScrollRef.current?.scrollTop ?? 0;
      }
      setIsEditingEffectiveDate(block.id === 'metadata-effectiveDate');
      setEditingBlock(block);
      setIsEditing(true);
      setActiveEditBlock(at);
      setSidebarTab('edit');
      setSidebarOpen(true);
      clearHover();
    },
    [canEditContract, clearHover, requestPremiumAccess, isEditing]
  );

  // Switch to a new block. If the editor is dirty, confirm first.
  const requestBlockSwitch = useCallback(
    (block: ReviewBlock, at: ReviewHighlight | null) => {
      if (isEditing && editorDirtyRef.current) {
        setPendingBlock({ block, at });
        return;
      }
      openBlockForEditing(block, at);
    },
    [isEditing, openBlockForEditing]
  );

  const handlePageClick = useCallback(() => {
    if (selectedField) {
      setSelectedField(null);
      return;
    }
    if (!hoveredBlock) return;
    const { block } = hoveredBlock;
    if (isEditing && editingBlock?.id === block.id) return;
    requestBlockSwitch(block, hoveredBlock);
  }, [hoveredBlock, isEditing, editingBlock, selectedField, requestBlockSwitch]);

  // Custom eased scroll — gives a buttery, controlled glide regardless of the
  // browser's native `behavior: 'smooth'` timing (which is uneven and can even
  // no-op in some scroll containers). `onArrive` fires when the glide settles,
  // letting us land the flash exactly as the block reaches centre. Honours the
  // user's reduced-motion preference by jumping instantly.
  const animateScrollTo = useCallback(
    (container: HTMLElement, to: number, onArrive?: () => void) => {
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);

      const maxScroll = container.scrollHeight - container.clientHeight;
      const target = Math.max(0, Math.min(to, maxScroll));
      const start = container.scrollTop;
      const distance = target - start;

      const prefersReduced =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (prefersReduced || Math.abs(distance) < 2) {
        container.scrollTop = target;
        onArrive?.();
        return;
      }

      // Duration scales with distance so short hops are snappy and long jumps
      // still glide, clamped to a tasteful range.
      const duration = Math.min(850, Math.max(380, Math.abs(distance) * 0.55));
      const startTime = performance.now();
      const easeInOutCubic = (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const step = (now: number) => {
        const progress = Math.min(1, (now - startTime) / duration);
        container.scrollTop = start + distance * easeInOutCubic(progress);
        if (progress < 1) {
          scrollAnimRef.current = requestAnimationFrame(step);
        } else {
          scrollAnimRef.current = null;
          onArrive?.();
        }
      };
      scrollAnimRef.current = requestAnimationFrame(step);
    },
    []
  );

  // Scroll the PDF viewport so a located block is centred vertically. Uses
  // viewport-relative geometry (rect.y is relative to its page), so it
  // works regardless of the current scroll offset or which buffer slot is live.
  const scrollToBlock = useCallback(
    (located: ReviewHighlight, onArrive?: () => void) => {
      const container = containerRef.current;
      const pageEl = activePageRefs.current.get(located.pageNumber);
      if (!container || !pageEl) {
        onArrive?.();
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const pageRect = pageEl.getBoundingClientRect();
      const blockTop = pageRect.top + located.rect.y;
      const delta =
        blockTop - containerRect.top - (container.clientHeight - located.rect.height) / 2;
      animateScrollTo(container, container.scrollTop + delta, onArrive);
    },
    [activePageRefs, animateScrollTo]
  );

  // Play the one-shot pulse on a located block and auto-clear it. Bumping the
  // nonce replays the animation even when re-selecting the same block.
  const flashBlockHighlight = useCallback((located: ReviewHighlight) => {
    setFlashBlock(located);
    setFlashNonce((n) => n + 1);
    setPageNumber(located.pageNumber);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      flashTimerRef.current = null;
      setFlashBlock(null);
    }, 2200);
  }, []);

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    };
  }, []);

  // When the block editor closes, restore the outline's previous scroll
  // position (layout effect → no flash to the top before the restore).
  useLayoutEffect(() => {
    if (!isEditing && editTabScrollRef.current) {
      editTabScrollRef.current.scrollTop = outlineScrollTopRef.current;
    }
  }, [isEditing]);

  const handleOutlineEdit = useCallback(
    (block: ReviewBlock) => {
      // Find the block in the rendered PDF so we can scroll to + flash it.
      const located = locateBlock(block);
      if (located) {
        // Glide to the block, then ping it the moment it settles at centre.
        scrollToBlock(located, () => flashBlockHighlight(located));
      }
      // Re-selecting the block already being edited: just navigate, don't reopen.
      if (isEditing && editingBlock?.id === block.id) return;
      // Pass the located state so the persistent edit highlight shows immediately.
      requestBlockSwitch(block, located);
    },
    [isEditing, editingBlock, requestBlockSwitch, locateBlock, scrollToBlock, flashBlockHighlight]
  );

  // Click-to-edit the document title from the page header (mirrors the outline
  // "Document Title" entry — scrolls to + flashes the title in the PDF too).
  const handleEditTitle = useCallback(() => {
    const titleText = editableDocument.metadata?.title?.trim();
    if (!titleText) return;
    handleOutlineEdit(
      toReviewBlock({
        blockId: 'metadata-title',
        blockPath: [],
        text: titleText,
        type: 'paragraph',
        isLeaf: true,
        isTitle: true,
        label: t('GENERATEREVIEW_VBLOCKS_TITLE', { defaultValue: 'Document Title' }),
      })
    );
  }, [editableDocument, handleOutlineEdit, t]);

  const handleSaveEdit = async (newText: string) => {
    if (!editingBlock) return;

    setIsSavingEdit(true);
    try {
      let updated: LegalDocument;
      // The shell carries blocks in normalized form; the JSON model's path and
      // title flag live on the mapping underneath.
      const mapping = fromReviewBlock(editingBlock);

      // Virtual blocks (title, effective date, signature-page fields) aren't in
      // the content tree — route them through the dedicated apply helper.
      if (isVirtualBlockId(editingBlock.id)) {
        updated = applyVirtualBlockEdit(
          editableDocument,
          editingBlock.id,
          newText,
          t('GENERATEREVIEW_DEFAULTS_EFFECTIVEDATELABEL')
        );
      } else {
        updated = updateBlockText(
          editableDocument,
          mapping.blockPath,
          newText,
          editingBlock.isTitle
        );
      }

      pushDocument(updated);
      setHasUnsavedChanges(true);
      setEditedBlockIds((prev) => new Set(prev).add(editingBlock.id));

      // Persist locally + regenerate the preview now (the user clicked Save).
      // The double buffer swaps it in seamlessly — no loader, no scroll jump.
      // Kick off the regen and keep its promise so we can hold the Save spinner
      // until the updated preview is actually on screen (clear "did it work?"
      // feedback rather than the editor vanishing into a silent re-render).
      persistReviewSnapshot(updated);
      const previewRendered = regenerateNow(updated);

      const editType = isEditingEffectiveDate
        ? 'effective_date'
        : editingBlock?.isTitle
          ? 'title'
          : 'text';
      trackDocumentEdited(templateSlug, editType);

      // Persist to OrgContractDocument on UDS — gated on draftId (legacy
      // sessionStorage-only docs have none). Run it alongside the render.
      if (draftId) {
        const updateInput: OrgContractDocumentUpdateInput = {
          Document: JSON.stringify(updated),
        };
        // Editing the document title also renames the record so the Contracts
        // list (which shows Name) and its search (which filters on
        // TemplateTitle) stay in sync — mirrors the row "Rename" action.
        if (editingBlock.id === 'metadata-title') {
          const newTitle = updated.metadata.title?.trim();
          if (newTitle) {
            updateInput.Name = newTitle;
            updateInput.TemplateTitle = newTitle;
          }
        }
        await updateContractDocument(draftId, updateInput);
        setHasUnsavedChanges(false);
      }

      // Wait for the regenerated preview to finish rendering + swap in, so the
      // Save button stays in its "Saving…" state for the whole processing
      // window instead of closing before the user sees the result.
      await previewRendered;

      // Close the editor and return to the outline.
      setIsEditing(false);
      setIsEditingEffectiveDate(false);
      setEditingBlock(null);
      setActiveEditBlock(null);
      editorDirtyRef.current = false;
    } catch (error) {
      console.error('Error saving edit:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save edit. Please try again.',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setIsEditingEffectiveDate(false);
    setEditingBlock(null);
    setActiveEditBlock(null);
    editorDirtyRef.current = false;
  }, []);

  // Unsaved-changes dialog handlers
  const handleDiscardAndSwitch = useCallback(() => {
    if (!pendingBlock) return;
    const { block, at } = pendingBlock;
    setPendingBlock(null);
    editorDirtyRef.current = false;
    openBlockForEditing(block, at);
  }, [pendingBlock, openBlockForEditing]);

  const handleSaveAndSwitch = useCallback(async () => {
    if (!pendingBlock) return;
    await handleSaveEdit(editorTextRef.current.trim());
    const { block, at } = pendingBlock;
    setPendingBlock(null);
    editorDirtyRef.current = false;
    openBlockForEditing(block, at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingBlock, openBlockForEditing]);

  // ---- Undo / redo --------------------------------------------------------

  // Persist a restored history snapshot (local + UDS, fire-and-forget) and
  // regenerate the preview now so the PDF reflects the change seamlessly.
  const persistRestored = useCallback(
    (doc: LegalDocument) => {
      setHasUnsavedChanges(true);
      persistReviewSnapshot(doc);
      regenerateNow(doc);
      if (draftId) {
        updateContractDocument(draftId, { Document: JSON.stringify(doc) }).catch((err) =>
          console.error('Undo/redo UDS persist failed', err)
        );
      }
    },
    [draftId, persistReviewSnapshot, regenerateNow]
  );

  const handleUndo = useCallback(() => {
    const prev = undoDocument();
    if (prev) persistRestored(prev);
  }, [undoDocument, persistRestored]);

  const handleRedo = useCallback(() => {
    const next = redoDocument();
    if (next) persistRestored(next);
  }, [redoDocument, persistRestored]);

  // Keyboard shortcuts for undo/redo (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditing) return; // editor owns its own keys while open
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (isMod && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isEditing, handleUndo, handleRedo]);

  // ---- Letterhead ---------------------------------------------------------

  const handleLetterheadConfirm = (letterheadConfig: LetterheadConfig | null) => {
    const updated = {
      ...editableDocument,
      letterhead: letterheadConfig || undefined,
    };
    pushDocument(updated);
    setHasUnsavedChanges(true);
    persistReviewSnapshot(updated);
    regenerateNow(updated);

    const isDefaultLetterhead =
      !letterheadConfig || letterheadConfig.imageDataUrl === resolvedLetterhead?.imageDataUrl;

    if (draftId && !isDefaultLetterhead) {
      updateContractDocument(draftId, { Document: JSON.stringify(updated) })
        .then(() => setHasUnsavedChanges(false))
        .catch((err) => {
          console.error('Letterhead UDS persist failed', err);
          toast({
            variant: 'destructive',
            title: 'Letterhead saved locally',
            description:
              'Could not sync the letterhead change to the server. It will retry when you save the document.',
          });
        });
    } else if (isDefaultLetterhead) {
      setHasUnsavedChanges(false);
    }
  };

  // ---- Save as draft ------------------------------------------------------

  const handleSaveAsDraft = useCallback(async () => {
    if (!draftId) return;
    if (saveDraftStatus === 'saving') return;

    if (saveDraftTimerRef.current) {
      clearTimeout(saveDraftTimerRef.current);
      saveDraftTimerRef.current = null;
    }

    setSaveDraftStatus('saving');
    try {
      await updateContractDocument(draftId, {
        Document: JSON.stringify(editableDocument),
        Status: 'draft',
      });
      setSaveDraftStatus('saved');
      setHasUnsavedChanges(false);
      saveDraftTimerRef.current = setTimeout(() => setSaveDraftStatus('idle'), 1800);
    } catch (err) {
      console.error('Save-as-draft failed', err);
      setSaveDraftStatus('error');
      saveDraftTimerRef.current = setTimeout(() => setSaveDraftStatus('idle'), 3000);
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: 'Could not save the document. Please try again.',
      });
    }
  }, [draftId, editableDocument, saveDraftStatus, toast]);

  useEffect(() => {
    return () => {
      if (saveDraftTimerRef.current) clearTimeout(saveDraftTimerRef.current);
    };
  }, []);

  // ---- Initialize / regenerate on prop change -----------------------------

  // Apply the resolved letterhead default, seed the history, and generate the
  // first preview. Also runs on a meaningful `document` prop change.
  useEffect(() => {
    if (isAuthenticated && isLoadingOrgLetterheads) return;
    if (lastDocumentSignatureRef.current === documentSignature) return;
    lastDocumentSignatureRef.current = documentSignature;

    const docWithLetterhead = {
      ...document,
      letterhead: document.letterhead || resolvedLetterhead,
    };

    resetDocument(docWithLetterhead);
    setHasUnsavedChanges(false);
    setEditedBlockIds(new Set());

    generatePreview(docWithLetterhead, { showLoader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, documentSignature, resolvedLetterhead, isAuthenticated, isLoadingOrgLetterheads]);

  // (Re)generate signature fields whenever the active page count or letterhead
  // changes — the metadata is placed relative to the real (rendered) page count
  // and the letterhead content area. Skipped for the locked/finalized branch.
  useEffect(() => {
    if (useFinalizedPdf) return;
    if (!activeNumPages || activeNumPages <= 0) return;
    if (signatories.length === 0) {
      setSignatureFields([]);
      return;
    }
    const metadataFields = generateSignatureFieldMetadata(
      signatories.map((sig, idx) => ({
        party: sig.role || `signatory_${idx}`,
        name: sig.name,
        email: sig.email,
      })),
      activeNumPages,
      editableDocument.letterhead
    );
    setSignatureFields(convertMetadataToFields(metadataFields, activeNumPages));
    // signatories is recomputed each render; key off the inputs that actually
    // change its placement to avoid a render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNumPages, editableDocument.letterhead, useFinalizedPdf]);

  // ---- Sidebar content (shared between desktop aside + mobile Sheet) ------

  const sidebarInner = (
    <ReviewSidebar
      sidebarTab={sidebarTab}
      onTabChange={setSidebarTab}
      isEditable={isEditable}
      isEditDisabled={isEditDisabled}
      isPremiumUnlocked={isPremiumUnlocked}
      editedBlockIds={editedBlockIds}
      details={reviewDetails}
      signatureFields={signatureFields}
      numPages={numPages}
      signatories={signatories}
      signatoryEventByEmail={signatoryEventByEmail}
      currentPage={pageNumber}
      onPageClick={scrollToPage}
      editTabScrollRef={editTabScrollRef}
      isEditing={isEditing}
      editingBlock={editingBlock}
      isSavingEdit={isSavingEdit}
      isEditingEffectiveDate={isEditingEffectiveDate}
      onSaveEdit={handleSaveEdit}
      onCancelEdit={handleCancelEdit}
      onEditorDirtyChange={(dirty) => {
        editorDirtyRef.current = dirty;
      }}
      onEditorTextChange={(text) => {
        editorTextRef.current = text;
      }}
      outline={outline}
      onEditBlock={handleOutlineEdit}
      openSections={openSections}
      onToggleSection={handleToggleSection}
    />
  );

  const sidebarFooter = (
    <SidebarFooter
      templateSlug={templateSlug}
      documentId={draftId}
      canUndo={canUndo}
      canRedo={canRedo}
      onUndo={handleUndo}
      onRedo={handleRedo}
      isEditable={isEditable}
      signatureFieldCount={signatureFields.length}
      isPremiumUnlocked={isPremiumUnlocked}
      onSendToSignature={
        hideDocumentActions && !isContractApproved
          ? () => setShowApprovalConfirmation(true)
          : handleSendToSignature
      }
      onRequestDownload={requestDownload}
      onRequestProtectedDownload={requestProtectedDownload}
      isDownloading={isDownloading}
      isDownloadingProtected={isDownloadingProtected}
      hideDownload={hideDocumentActions}
      isApprovalPage={hideDocumentActions}
      isContractApproved={isContractApproved}
      isPendingApproval={!hideDocumentActions && isPendingApproval}
    />
  );

  return (
    <ReviewShell
      scrollContainerRef={containerRef}
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={setSidebarOpen}
      sidebar={sidebarInner}
      sidebarFooter={isEditing ? undefined : sidebarFooter}
      header={
        <TemplatePDFReviewHeader
          templateTitle={editableDocument.metadata?.title || templateTitle}
          templateSlug={templateSlug}
          documentId={draftId}
          onEditTitle={canEditContract ? handleEditTitle : undefined}
          numPages={numPages}
          signatureFields={signatureFields}
          signatoriesLength={signatories?.length}
          hasUnsavedChanges={hasUnsavedChanges}
          isAuthenticated={isAuthenticated}
          onBack={onBack}
          onLetterheadClick={() => {
            if (requestPremiumAccess('letterhead')) setShowLetterheadDialog(true);
          }}
          setShowDownloadPrompt={(show: boolean) => {
            if (show && !requestPremiumAccess('download')) return;
            setShowDownloadPrompt(show);
          }}
          handleSendToSignature={
            hideDocumentActions && !isContractApproved
              ? () => setShowApprovalConfirmation(true)
              : handleSendToSignature
          }
          onSaveDraft={handleSaveAsDraft}
          saveDraftStatus={saveDraftStatus}
          canSaveDraft={!!draftId && isEditable}
          isEditable={isEditable}
          documentStatus={documentStatus}
          handleDownloadPdf={() => {
            if (!requestPremiumAccess('download')) return;
            void handleDownloadPdf();
          }}
          isDownloading={isDownloading}
          hideDocumentActions={hideDocumentActions}
          isApprovalPage={hideDocumentActions}
          isContractApproved={isContractApproved}
          hasSentForApproval={contractDoc?.HasSentForApproval === true}
        />
      }
      // Soft gate — authenticated users never see this.
      banner={
        isGuestUnpaid && draftId ? (
          <GuestPaywallBanner onPay={() => setShowPaymentDialog(true)} />
        ) : undefined
      }
      toolbar={
        <PdfPreviewToolbar
          scale={scale}
          isPdfStale={isPdfStale}
          isLoadingPdf={isLoadingPdf}
          onRegenerateNow={() => regenerateNow(editableDocument)}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitPage={handleFitPage}
          onFitWidth={handleFitWidth}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
      }
      viewer={
        <PdfReviewViewer
          scale={scale}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          pageWidthPt={docPageWidthPt}
          pageHeightPt={docPageHeightPt}
          templateTitle={editableDocument.metadata?.title || templateTitle}
          isLoadingPdf={isLoadingPdf}
          hasPdf={hasPdf}
          finalizedPdfUrl={useFinalizedPdf ? sigDocumentUrl : null}
          numPages={numPages}
          onFinalizedLoadSuccess={onFinalizedLoadSuccess}
          slotUrls={slotUrls}
          activeSlot={activeSlot}
          pageRefsA={pageRefsA}
          pageRefsB={pageRefsB}
          onSlotLoaded={handleSlotLoaded}
          onSlotReady={handleSlotReady}
          isEditable={canEditContract}
          hasUnsavedChanges={hasUnsavedChanges}
          hoveredBlock={hoveredBlock}
          activeEditBlock={activeEditBlock}
          flashBlock={flashBlock}
          flashNonce={flashNonce}
          isEditing={isEditing}
          editingBlockId={editingBlock?.id ?? null}
          onPageClick={handlePageClick}
          onTextHover={handleTextHover}
          onTextLeave={handleTextLeave}
          resolveBlockFromTouch={resolveBlockFromTouch}
          requestBlockSwitch={requestBlockSwitch}
          signatureFields={signatureFields}
          signatories={signatories}
          selectedField={selectedField}
          onSelectField={setSelectedField}
          onSignatureFieldsChange={handleSignatureFieldsChange}
        />
      }
      mobileActionBar={
        <MobileActionBar
          templateSlug={templateSlug}
          documentId={draftId}
          isEditable={isEditable}
          signatureFieldCount={signatureFields.length}
          onRequestDownload={requestDownload}
          onSendToSignature={
            hideDocumentActions && !isContractApproved
              ? () => setShowApprovalConfirmation(true)
              : handleSendToSignature
          }
          isDownloading={isDownloading}
          hideDownload={hideDocumentActions}
          isApprovalPage={hideDocumentActions}
          isContractApproved={isContractApproved}
          isPendingApproval={!hideDocumentActions && isPendingApproval}
        />
      }
      overlay={isPreparingContract ? <PreparingContract /> : undefined}
      dialogs={
        <>
          <ConfirmationModal
            open={showApprovalConfirmation}
            onOpenChange={setShowApprovalConfirmation}
            title={t("GENERATEREVIEW_ACTIONS_APPROVECONTRACT")}
            description={t("GENERATEREVIEW_ACTIONS_APPROVECONTRACT_DESCRIPTION")}
            confirmText="GENERATEREVIEW_ACTIONS_APPROVECONTRACT"
            onConfirm={() => void handleApproveContract()}
            preventAutoClose
            isLoading={isApprovingContract}
          />

          {!hideDocumentActions && (
            <DownloadPDFDialog
              open={showDownloadPrompt}
              onOpenChange={setShowDownloadPrompt}
              signatoriesLength={signatories?.length}
              handleSendToSignature={handleSendToSignature}
              handleDownloadPdf={handleDownloadPdf}
            />
          )}

          {!hideDocumentActions && (
            <LetterheadUploadDialog
              open={showLetterheadDialog}
              onClose={() => setShowLetterheadDialog(false)}
              onConfirm={handleLetterheadConfirm}
              existingLetterhead={editableDocument.letterhead}
            />
          )}

          {/* Unsaved-changes confirmation when switching blocks mid-edit. */}
          <UnsavedChangesDialog
            open={pendingBlock !== null}
            onOpenChange={(open) => {
              if (!open) setPendingBlock(null);
            }}
            onDiscard={handleDiscardAndSwitch}
            onSaveAndSwitch={handleSaveAndSwitch}
          />

          {isGuestUnpaid && draftId && (
            <PaymentDialog
              open={showPaymentDialog}
              onOpenChange={(open) => (open ? setShowPaymentDialog(true) : dismissPaymentDialog())}
              documentId={draftId}
              onPaid={() => {
                onPremiumPaid();
                if (triggeredFeature === 'download') {
                  setShowDownloadPrompt(true);
                } else if (triggeredFeature === 'letterhead') {
                  setShowLetterheadDialog(true);
                }
              }}
            />
          )}
        </>
      }
    />
  );
};

export default TemplatePDFReview;
